var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import styles from './input.styles';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { classMap } from 'lit/directives/class-map.js';
import { MFElement } from '../mfelement/mfelement';
let PInput = class PInput extends MFElement {
    constructor() {
        super(...arguments);
        this.size = 'normal';
        this.id = '';
        this.value = '';
        this.required = false;
        this.readonly = false;
        this.labelHidden = false;
        this.labelLeft = false;
    }
    static { this.styles = styles; }
    /**
     * Handle change
     * @returns {void}
     */
    handleChange() {
        this.value = this.input.value;
        this.dispatchEvent(new CustomEvent('p-change', { bubbles: true, composed: true }));
    }
    /**
     * Handle blur
     * @returns {void}
     */
    handleBlur() {
        this.dispatchEvent(new CustomEvent('p-blur', { bubbles: true, composed: true }));
    }
    /**
     * Handle focus
     * @returns {void}
     */
    handleFocus() {
        this.dispatchEvent(new CustomEvent('p-focus', { bubbles: true, composed: true }));
    }
    /**
     * Set focus
     * @returns {void}
     */
    setFocus() {
        this.input.focus();
    }
    /**
     * Handle input
     * @returns {void}
     */
    handleInput() {
        this.value = this.input.value;
        this.dispatchEvent(new CustomEvent('p-input', { bubbles: true, composed: true }));
    }
    /**
     * Print aria-labelledby
     * @returns {string|null} Returns aria-labelledby or null
     */
    printAriaLabelledBy() {
        let ariaLabelBy = null;
        if (this.error) {
            ariaLabelBy = `error-${this.id}`;
        }
        if (this.help) {
            ariaLabelBy = `${ariaLabelBy ? ariaLabelBy + ' ' : ''}help-${this.id}`;
        }
        return ariaLabelBy;
    }
    /**
     * Handle slot change
     *
     * @param {any} e Event
     * @returns {void}
     */
    handleSlotChange(e) {
        if (e.target.assignedElements({ flatten: true })) {
            this.input.classList.add('input--prefix');
        }
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const classes = {
            [`${this.size}`]: true,
            ['has-error']: this.error,
            ['label-hidden']: this.labelHidden,
            ['label-left']: this.labelLeft,
            ['no-help']: !this.help ? true : false
        };
        return html ` <div class="input-wrap${classMap(classes)}"> <div class="label-wrap"> <label for=${this.id} class="label${this.labelHidden ? ' sr-only' : ''}"> ${this.label} ${this.required ? html `<span aria-hidden="true">*</span>` : ''}
          </label>
          ${this.help ? html `<div class="help" id="help-${this.id}">${this.help}</div>` : ''}
        </div>
        <div class="input-inner">
          <slot name="prefix" @slotchange=${this.handleSlotChange}></slot>
          <input
            class="input"
            aria-labelledby=${ifDefined(this.printAriaLabelledBy())}
            type=${this.type ? this.type : 'text'}
            id=${this.id}
            name=${this.id}
            .value=${live(this.value)}
            placeholder=${ifDefined(this.placeholder)}
            aria-required=${ifDefined(this.ariaRequired ? 'true' : null)}
            ?required=${this.required}
            aria-invalid=${ifDefined(this.error ? 'true' : null)}
            maxlength=${ifDefined(this.maxlength)}
            ?readonly=${this.readonly}
            @change=${this.handleChange}
            @blur=${this.handleBlur}
            @focus=${this.handleFocus}
            @input=${this.handleInput}
          />
          <slot name="suffix"></slot>
          ${this.error
            ? html `<div role="alert" aria-live=${ifDefined(this.ariaLive ? this.ariaLive : null)} aria-atomic="true" class="error" id="error-${this.id}" > <span class="sr-only">Error: </span>${this.error} </div>`
            : ''}
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], PInput.prototype, "type", void 0);
__decorate([
    property({ type: String })
], PInput.prototype, "label", void 0);
__decorate([
    property({ type: String })
], PInput.prototype, "size", void 0);
__decorate([
    property({ type: String })
], PInput.prototype, "help", void 0);
__decorate([
    property({ type: String, reflect: true })
], PInput.prototype, "error", void 0);
__decorate([
    property({ type: String, attribute: 'input-id' })
], PInput.prototype, "id", void 0);
__decorate([
    property({ reflect: true })
], PInput.prototype, "value", void 0);
__decorate([
    property({ type: String })
], PInput.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PInput.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PInput.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean, attribute: 'label-hidden' })
], PInput.prototype, "labelHidden", void 0);
__decorate([
    property({ type: Number, reflect: true })
], PInput.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, attribute: 'label-left' })
], PInput.prototype, "labelLeft", void 0);
__decorate([
    query('.input')
], PInput.prototype, "input", void 0);
PInput = __decorate([
    customElement('p-input')
    /**
     * PInput component
     *
     * @class PInput
     * @typedef {PInput}
     * @extends {LitElement}
     */
], PInput);
export { PInput };
